package com.patronat.top.ui.molecules.alerts

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.painter.Painter // Імпорт Painter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.ui.theme.AlertAccent
import com.patronat.top.ui.theme.AlertContainer
import com.patronat.top.ui.theme.AlertStrip
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.White
import com.patronat.top.ui.theme.rememberAppTypography

@Composable
fun AppAlert(
    title: String,
    message: String,
    icon: Painter,
    modifier: Modifier = Modifier,
    containerColor: Color = AlertContainer,
    accentColor: Color = AlertAccent,
    stripColor: Color = AlertStrip
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(4.dp))
            .background(containerColor)
            .height(IntrinsicSize.Min)
    ) {
        Row(
            modifier = Modifier.fillMaxSize(),
            verticalAlignment = Alignment.CenterVertically
        ) {

            Column(
                modifier = Modifier
                    .weight(1f)
                    .padding(vertical = 12.dp, horizontal = 16.dp)
            ) {
                Text(
                    text = title,
                    style = rememberAppTypography().bodyLarge.copy(
                        fontWeight = FontWeight.Bold,
                        color = MineShaft900,
                        fontSize = 16.sp
                    )
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = message,
                    style = rememberAppTypography().bodyMedium.copy(
                        color = MineShaft900,
                        fontSize = 14.sp,
                        lineHeight = 18.sp
                    )
                )
            }

            Box(
                modifier = Modifier
                    .height(40.dp)
                    .width(40.dp)
                    .background(accentColor),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = icon,
                    contentDescription = null,
                    tint = White,
                    modifier = Modifier.size(32.dp)
                )
            }

            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .heightIn(min = 72.dp)
                    .width(5.dp)
                    .background(stripColor)
            )
        }
    }
}

@Composable
fun AppMobileAlert(
    title: String,
    message: String,
    modifier: Modifier = Modifier,
    icon: Painter? = null,
    containerColor: Color = AlertContainer,
    accentColor: Color = AlertAccent
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(2.dp))
            .background(containerColor)
            .padding(start = 6.dp, top = 14.dp, end = 14.dp, bottom = 14.dp),
        verticalAlignment = Alignment.Top
    ) {
        Box(
            modifier = Modifier
                .size(34.dp)
                .clip(RoundedCornerShape(4.dp))
                .background(accentColor)
                .border(1.dp, White.copy(alpha = 0.86f), RoundedCornerShape(4.dp)),
            contentAlignment = Alignment.Center
        ) {
            if (icon != null) {
                Icon(
                    painter = icon,
                    contentDescription = null,
                    tint = White,
                    modifier = Modifier.size(24.dp)
                )
            } else {
                AlertWarningGlyph(
                    color = White,
                    modifier = Modifier.size(24.dp)
                )
            }
        }

        Spacer(modifier = Modifier.width(12.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = title,
                style = rememberAppTypography().bodyLarge.copy(
                    fontWeight = FontWeight.Bold,
                    color = MineShaft900,
                    fontSize = 16.sp,
                    lineHeight = 20.sp
                )
            )

            Spacer(modifier = Modifier.height(6.dp))

            Text(
                text = message,
                style = rememberAppTypography().bodyMedium.copy(
                    color = MineShaft900,
                    fontSize = 14.sp,
                    lineHeight = 18.sp
                )
            )
        }
    }
}

@Composable
private fun AlertWarningGlyph(
    color: Color,
    modifier: Modifier = Modifier
) {
    Canvas(modifier = modifier) {
        val strokeWidth = 1.7.dp.toPx()
        val triangle = Path().apply {
            moveTo(size.width * 0.5f, size.height * 0.12f)
            lineTo(size.width * 0.9f, size.height * 0.84f)
            lineTo(size.width * 0.1f, size.height * 0.84f)
            close()
        }

        drawPath(
            path = triangle,
            color = color,
            style = Stroke(
                width = strokeWidth,
                cap = StrokeCap.Round,
                join = StrokeJoin.Round
            )
        )

        drawLine(
            color = color,
            start = androidx.compose.ui.geometry.Offset(size.width * 0.5f, size.height * 0.36f),
            end = androidx.compose.ui.geometry.Offset(size.width * 0.5f, size.height * 0.58f),
            strokeWidth = strokeWidth,
            cap = StrokeCap.Round
        )

        drawCircle(
            color = color,
            radius = 1.25.dp.toPx(),
            center = androidx.compose.ui.geometry.Offset(size.width * 0.5f, size.height * 0.7f)
        )
    }
}
